import React, { useEffect, useState, Component } from 'react';
import { X, User, Building2, Users, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const AgencyModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referred, setReferred] = useState('');
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
        setReferred('');
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    // Initial celebratory burst
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    // Continuous rain from the top
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT YOU', `Full name: ${get('fullName')}`, `Role / title: ${get('role')}`, `Email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, '', 'YOUR AGENCY', `Agency name: ${get('agencyName')}`, `Website: ${get('website')}`, `Number of creators: ${get('creators')}`, '', 'YOUR CREATORS', `Roster link: ${get('rosterLink')}`, `Primary niche: ${get('niche')}`, `Notes: ${get('notes')}`, '', 'DISCOVERY', `How did you hear about us?: ${get('discoverySource')}`, '', 'REFERRAL', `Referred by an existing Zano user?: ${get('referred')}`, `Referred by: ${get('referredBy')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New agency listing request',
      from_name: 'Zano — List Your Agency form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-29" data-mp="iaqt628xxrup">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-30" data-mp="ranafit03ofq">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-31" data-mp="116yqmjvpoyfg">
          <X size={20} data-id="element-32" data-mp="w6uh5dt813yq" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-33" data-mp="19zk9dzzcf54a">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-34" data-mp="15xyn5o0x5ehf">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-35" data-mp="1jxgiyncmts0a" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-36" data-mp="1ezophowc9xtz">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-37" data-mp="1ym00nvqrtj72">
              Our team will review your request and respond within 48 hours. If
              approved, you'll receive an invite to the marketplace to create
              your account for your agency — and you'll be able to invite the
              rest of your team.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6" data-id="element-38" data-mp="1tnorzoc7peqf">
              <Mail size={14} className="text-zano-pink" data-id="element-39" data-mp="1fsmae3y060xs" />
              <span data-id="element-40" data-mp="1klyz0e2om7qj">
                Sent to{' '}
                <span className="font-medium text-zano-charcoal" data-id="element-41" data-mp="15wb0tlpsrp71">
                  {SUPPORT_EMAIL}
                </span>
              </span>
            </div>
            <Button variant="primary" className="w-full justify-center" onClick={onClose} data-id="element-42" data-mp="1avw0jycsk1kt">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-43" data-mp="10zaq2xi05y9l">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-44" data-mp="33kwwx343bdii">
              List Your Agency
            </h3>
            <p className="text-gray-600 mb-6 text-sm" data-id="element-45" data-mp="2ysd9leflp40i">
              Join 400+ talent agencies on Zano. Tell us a little about you,
              your agency, and your creators to get started.
            </p>
            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-46" data-mp="2owjzs2t9nomf">
              {/* About you */}
              <div data-id="element-47" data-mp="3mi7lnts3iojp">
                <div className="flex items-center gap-2 mb-3" data-id="element-48" data-mp="3mgmxqn0ae3ut">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-49" data-mp="3hkxy6jo9omyi">
                    <User size={14} data-id="element-50" data-mp="3cjs34f74wnlt" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-51" data-mp="3cme7k5yqamzt">
                    About you
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-52" data-mp="216ftfnop1arq">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-53" data-mp="2131s8irrn7lv">
                    <div data-id="element-54" data-mp="1x286nw1sq4w5">
                      <label className={labelClass} data-id="element-55" data-mp="1x65omecn0qeg">Full name</label>
                      <input name="fullName" type="text" required className={inputClass} data-id="element-56" data-mp="cnbr1f0pau3h" />
                    </div>
                    <div data-id="element-57" data-mp="qd5djqoqh2a1">
                      <label className={labelClass} data-id="element-58" data-mp="qdotj1utpglf">Role / title</label>
                      <input name="role" type="text" placeholder="e.g. Talent Manager" className={inputClass} data-id="element-59" data-mp="vc8kjgo9xikn" />
                    </div>
                  </div>
                  <div data-id="element-60" data-mp="14atyszmpqbfm">
                    <label className={labelClass} data-id="element-61" data-mp="19c14mt5ap0jn">Email</label>
                    <input name="email" type="email" required className={inputClass} data-id="element-62" data-mp="19b88sn7vjvga" />
                  </div>
                  <div data-id="element-63" data-mp="3b5h0q5k5j7m0">
                    <label className={labelClass} data-id="element-64" data-mp="1gvaiozoqxnf1">LinkedIn profile URL</label>
                    <input name="linkedin" type="url" placeholder="https://linkedin.com/in/you" className={inputClass} data-id="element-65" data-mp="1gt649nt8160i" />
                  </div>
                </div>
              </div>

              {/* Your agency */}
              <div data-id="element-66" data-mp="xw4uzqsmwjqr">
                <div className="flex items-center gap-2 mb-3" data-id="element-67" data-mp="xxpp5fb1e0o3">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-68" data-mp="tv2xpfia2hrf">
                    <Building2 size={14} data-id="element-69" data-mp="txfkq72w7ibc" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-70" data-mp="17wfaji256393">
                    Your agency
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-71" data-mp="2ja10v6jqmaw7">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-72" data-mp="2j8pssuj9egza">
                    <div data-id="element-73" data-mp="2ebpnwmy5bk9r">
                      <label className={labelClass} data-id="element-74" data-mp="2ea4w3537uffl">Agency name</label>
                      <input name="agencyName" type="text" required className={inputClass} data-id="element-75" data-mp="2vtpuw5sd6l" />
                    </div>
                    <div data-id="element-76" data-mp="3n9fr4mqjn3bz">
                      <label className={labelClass} data-id="element-77" data-mp="3na7nw0vvn73u">Website</label>
                      <input name="website" type="text" placeholder="agency.com" className={inputClass} data-id="element-78" data-mp="3s7hjjy9v0f04" />
                    </div>
                  </div>
                  <div data-id="element-79" data-mp="a24jebjzr5n5">
                    <label className={labelClass} data-id="element-80" data-mp="a0tg0cugjzwk">Number of creators</label>
                    <select name="creators" className={inputClass} data-id="element-81" data-mp="ezmmvmf8osnr">
                      <option data-id="element-82" data-mp="12q0bf2ym89dl">1-10</option>
                      <option data-id="element-83" data-mp="12p8hrxokwlpy">11-50</option>
                      <option data-id="element-84" data-mp="12o5us0udn3fb">51-100</option>
                      <option data-id="element-85" data-mp="17hs5hngzcx8h">100+</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Your creators */}
              <div data-id="element-86" data-mp="2v2rf7sqywsc6">
                <div className="flex items-center gap-2 mb-3" data-id="element-87" data-mp="2v39i3884y4t2">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-88" data-mp="2h38lw62tfljx">
                    <Users size={14} data-id="element-89" data-mp="2h5b80f0ukujd" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-90" data-mp="2l3ipjc34xzvm">
                    Your creators
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-91" data-mp="2c45e4dvgb189">
                  <div data-id="element-92" data-mp="2c5703de0984r">
                    <label className={labelClass} data-id="element-93" data-mp="2c5ggqad5u2xs">
                      Roster link{' '}
                      <span className="text-gray-400 font-normal" data-id="element-94" data-mp="1xgu5ajqnlvbu">
                        (optional)
                      </span>
                    </label>
                    <input name="rosterLink" type="text" placeholder="Link to your roster or media kit" className={inputClass} data-id="element-95" data-mp="1oe1c2axwv9jd" />
                  </div>
                  <div data-id="element-96" data-mp="19tylod677jxb">
                    <label className={labelClass} data-id="element-97" data-mp="14t13i0l6upvt">Primary niche</label>
                    <select name="niche" defaultValue="" className={inputClass} data-id="element-98" data-mp="14uvqrljxmu32">
                      <option value="" disabled data-id="element-99" data-mp="1iu3qcfk0c47s">
                        Select a niche
                      </option>
                      <option data-id="element-100" data-mp="1et1so35qo07x">Fashion</option>
                      <option data-id="element-101" data-mp="1spyfzumnezk2">Beauty</option>
                      <option data-id="element-102" data-mp="1sovmdexxyj0g">Lifestyle</option>
                      <option data-id="element-103" data-mp="1ns5br74wofmf">Tech</option>
                      <option data-id="element-104" data-mp="1nrddfojxc3sg">Gaming</option>
                      <option data-id="element-105" data-mp="22g9v8dbs8v69">Fitness</option>
                      <option data-id="element-106" data-mp="22g05mqungq13">Food</option>
                      <option data-id="element-107" data-mp="1xhgjoqg23e8q">Travel</option>
                      <option data-id="element-108" data-mp="1xiriebun9vsz">Mixed / Multiple</option>
                    </select>
                  </div>
                  <div data-id="element-109" data-mp="2bhr87mtw8gqq">
                    <label className={labelClass} data-id="element-110" data-mp="2bf53rw2auhcq">
                      Anything else?{' '}
                      <span className="text-gray-400 font-normal" data-id="element-111" data-mp="26g1w03uxpx12">
                        (optional)
                      </span>
                    </label>
                    <textarea name="notes" rows={2} placeholder="Tell us more about your roster" className={inputClass} data-id="element-112" data-mp="194v0slql1yyc" />
                  </div>
                </div>
              </div>

              {/* Referral */}
              <div data-id="element-113" data-mp="1muonoyij295f">
                <div className="space-y-4" data-id="element-114" data-mp="1mxazxbgd5ctz">
                  <div data-id="element-115" data-mp="1rti3ixlaec8r">
                    <label className={labelClass} htmlFor="discoverySource" data-id="element-116" data-mp="1rsq7jlns2chy">
                      How did you hear about us?
                    </label>
                    <select id="discoverySource" name="discoverySource" defaultValue="" className={inputClass} data-id="element-117" data-mp="20uppcg6m9kly">
                      <option value="" disabled data-id="element-118" data-mp="6g6qqop74197">
                        Select an option
                      </option>
                      <option data-id="element-119" data-mp="3v4ee8yanp6p8">LinkedIn</option>
                      <option data-id="element-120" data-mp="3qc3cdpwcvau1">Google</option>
                      <option data-id="element-121" data-mp="3qbbjimu57r7g">ChatGPT</option>
                      <option data-id="element-122" data-mp="3ld09aazxreop">Email outreach</option>
                      <option data-id="element-123" data-mp="3ldtjxn20moc4">Zano newsletter</option>
                      <option data-id="element-124" data-mp="3hborgrnazxr2">Influencer Marketing Hub</option>
                      <option data-id="element-125" data-mp="3hd9kudxvtanc">Net Influencer</option>
                      <option data-id="element-126" data-mp="hwtdsrrocnr0">WIIM</option>
                    </select>
                  </div>
                  <div data-id="element-127" data-mp="cwp0fam2c950">
                    <label className={labelClass} data-id="element-128" data-mp="cyi7gr0l8vdj">
                      Were you referred by an existing Zano user?
                    </label>
                    <select name="referred" value={referred} onChange={e => setReferred(e.target.value)} className={inputClass} data-id="element-129" data-mp="8xg6o5r0wb9g">
                      <option value="" disabled data-id="element-130" data-mp="2omhrdo87b03p">
                        Select an option
                      </option>
                      <option data-id="element-131" data-mp="gci4aa3t5gyw">No</option>
                      <option data-id="element-132" data-mp="l4rp5bq2fqub">Yes</option>
                    </select>
                  </div>
                  {referred === 'Yes' && <div data-id="element-133" data-mp="7eeirdstok73">
                      <label className={labelClass} data-id="element-134" data-mp="7exyqoywwyih">Who referred you?</label>
                      <input name="referredBy" type="text" placeholder="Name of the Zano user who referred you" className={inputClass} data-id="element-135" data-mp="bg01vgvy9upq" />
                    </div>}
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-136" data-mp="q5z17mz8rc6s">
                <input type="checkbox" id="tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-137" data-mp="v1wfzouuwahg" />
                <label htmlFor="tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-138" data-mp="37c63ar3nc9mv">
                  By checking this box, I acknowledge and agree to the{' '}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="font-medium text-zano-pink underline underline-offset-2 hover:text-zano-pinkDark" data-id="element-139" data-mp="3halsupn0zau9">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-zano-pink underline underline-offset-2 hover:text-zano-pinkDark" data-id="element-140" data-mp="2jmli01k0t0af">
                    Privacy Policy
                  </a>{' '}
                  by Zano Platform. I acknowledge that Zano Platform may collect, process, and store my personal data provided in this form for the purpose of evaluating my request and providing access to the platform. I understand that my data will be handled in accordance with the Zano Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-141" data-mp="9r0j98wz7zto">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-142" data-mp="ejk6kxlgu9bo">
                {submitting ? 'Sending…' : 'Submit'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};